import React, { useState } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import PropTypes from 'prop-types'
import { Modal } from 'antd-v4'
import { Tabs } from 'antd'
import { ExportProductButton } from './ExportProductButton'
import { ExportProductsPricesButton } from './ExportProductsPricesButton'
import { ImportProductsPricesDropZone } from './ImportProductsPricesDropZone'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { ExportProductsWarningModal } from './ExportProductsWarningModal'
import { ImportProductsDropZone } from './ImportProductsDropzone'
import { ProductModels } from '@getgreenline/products'
import { CSVProductHeaders } from '../../../../constants/CSVHeaders'
import { HelpLinks } from '../../../../constants/ExternalLinks'

const { TabPane } = Tabs

const uneditableProductDetailsColumns = [
  CSVProductHeaders.PARENT_ID,
  CSVProductHeaders.PARENT_SKU,
  CSVProductHeaders.PARENT_NAME,
  CSVProductHeaders.FULL_NAME,
  CSVProductHeaders.IS_MASTER_PRODUCT,
  CSVProductHeaders.IS_TRACKING_INVENTORY,
  CSVProductHeaders.PRICE,
  CSVProductHeaders.DEPOSIT_FEE,
  CSVProductHeaders.TAX,
  CSVProductHeaders.PURCHASE_PRICE,
  CSVProductHeaders.TRACK_LOTS,
  CSVProductHeaders.CREATED,
  CSVProductHeaders.UPDATED,
]

interface Props {
  isModalVisible: boolean
  closeModal: () => void
  afterImport: () => void
  currentCompanyStore?: CurrentCompanyStore
}

const propTypes = {
  isModalVisible: PropTypes.bool,
  closeModal: PropTypes.func,
  afterImport: PropTypes.func,
}

const ImportExportProductsModalComponent: React.FC<Props> = ({
  isModalVisible,
  closeModal,
  afterImport,
  currentCompanyStore,
}) => {
  const [showPricesSuccessMessage, setShowPricesSuccessMessage] = useState<boolean>(false)
  const [pricesErrorMessages, setPricesErrorMessages] = useState<
    string[] | ProductModels.IBulkError[]
  >([])
  const [pricesErrorTitle, setPricesErrorTitle] = useState<string>('')
  const [showDetailsSuccessMessage, setShowDetailsSuccessMessage] = useState<boolean>(false)
  const [detailsErrorMessages, setDetailsErrorMessages] = useState<
    string[] | ProductModels.IBulkError[]
  >([])
  const [detailsErrorTitle, setDetailsErrorTitle] = useState<string>('')
  const [isExportProductsWarningModalOpen, setIsExportProductsWarningModalOpen] =
    useState<boolean>(false)

  const handleCloseModal = () => {
    closeModal()
    setShowPricesSuccessMessage(false)
    setPricesErrorMessages([])
    setPricesErrorTitle('')
    setDetailsErrorMessages([])
    setDetailsErrorTitle('')
  }

  return (
    <Modal
      title='Import / Export'
      visible={isModalVisible}
      onCancel={handleCloseModal}
      maskClosable={false}
      footer={null}
      width={600}
    >
      <Tabs defaultActiveKey='products'>
        <TabPane tab='Product Details' key='products'>
          {currentCompanyStore?.canUseBulkProductUpdate ? (
            <>
              <p>
                Export your product details, make changes, then import them to bulk update your
                products.
              </p>
              <p>
                <strong>All columns are editable, except:</strong>
              </p>
              <ul>
                {uneditableProductDetailsColumns.map((columnHeader, index) => (
                  <li key={index}>{columnHeader}</li>
                ))}
              </ul>

              <p>
                Parent SKU, Parent Name, Brand and Category can only be updated on the parent row of
                a product. Each variant product will include a parent ID.
              </p>
              <p>
                Categories must be created ahead of time.<br></br>
                Subcategories must be identified using a - separator (for ex: Dried Flower - Indica)
              </p>
              <p>
                An ID column in provided for each product. This column must be included to update an
                existing product.
              </p>
              <p>
                <strong>
                  <a
                    // To update with direct link to Product Details Bulk Update help page once it's created: https://blazesolutions.atlassian.net/browse/GR-9971
                    href={HelpLinks.PRODUCTS_ADD_EDIT}
                    target='_blank'
                    rel='noreferrer'
                    style={{ textDecoration: 'underline' }}
                  >
                    Learn more about editing product details exports
                  </a>
                </strong>
              </p>
              <p>* Filters applied to the Product List will be applied to exports.</p>

              <BlazeButton
                buttonProps={{ onClick: () => setIsExportProductsWarningModalOpen(true) }}
                titleProps={{ title: 'Export CSV' }}
                iconProps={{ icon: 'download' }}
              />

              <ImportProductsDropZone
                showSuccessMessage={showDetailsSuccessMessage}
                setShowSuccessMessage={setShowDetailsSuccessMessage}
                errorMessages={detailsErrorMessages}
                setErrorMessages={setDetailsErrorMessages}
                errorTitle={detailsErrorTitle}
                setErrorTitle={setDetailsErrorTitle}
                afterImport={afterImport}
              />
            </>
          ) : (
            <div>
              <p>Export your product list.</p>
              <p>* Filters applied to the Product List will be applied to exports</p>
              <ExportProductButton />
            </div>
          )}
        </TabPane>

        <TabPane tab='Prices' key='prices'>
          <p>Export your prices, make changes, then import them to bulk update your products.</p>
          <div className='mb-3'>
            <div>The following columns are editable:</div>
            <div>- Deposit Fee</div>
            <div>- Wholesale Cost</div>
            <div>- Retail Price</div>
          </div>
          <p>For location-specific entries, only the retail price column is editable.</p>
          <p>
            <strong>
              <a
                href={HelpLinks.PRODUCTS_BULK_UPDATE_PRICE}
                target='_blank'
                rel='noreferrer'
                style={{ textDecoration: 'underline' }}
              >
                Learn more about editing price exports
              </a>
            </strong>
          </p>
          <p>* Filters applied to the Product List will be applied to exports.</p>

          <ExportProductsPricesButton />

          <ImportProductsPricesDropZone
            showSuccessMessage={showPricesSuccessMessage}
            setShowSuccessMessage={setShowPricesSuccessMessage}
            errorMessages={pricesErrorMessages}
            setErrorMessages={setPricesErrorMessages}
            errorTitle={pricesErrorTitle}
            setErrorTitle={setPricesErrorTitle}
            afterImport={afterImport}
          />
        </TabPane>
      </Tabs>
      <ExportProductsWarningModal
        visible={isExportProductsWarningModalOpen}
        onCancel={() => {
          setIsExportProductsWarningModalOpen(false)
        }}
      />
    </Modal>
  )
}

ImportExportProductsModalComponent.propTypes = propTypes

export const ImportExportProductsModal = inject('currentCompanyStore')(
  observer(ImportExportProductsModalComponent),
)
